/**
 * ComponenteListaDocumentos - Listagem de Documentos com Ações
 * 
 * CONTEXTO DE NEGÓCIO:
 * Este componente exibe a lista de documentos jurídicos em formato de tabela.
 * Fornece ações para cada documento (visualizar, deletar).
 * 
 * FUNCIONALIDADES:
 * - Tabela responsiva com documentos
 * - Informações: nome, tipo, tamanho, data, status
 * - Ações por documento: visualizar detalhes, deletar
 * - Confirmação antes de deletar
 * - Estados vazios (sem documentos, filtros sem resultados)
 * - Paginação
 * 
 * RESPONSABILIDADES:
 * - Renderizar lista de documentos
 * - Fornecer ações de gerenciamento
 * - Exibir status visual (badges coloridos)
 * - Comunicar ações ao componente pai
 * 
 * INTEGRAÇÃO:
 * - Usado pela PaginaHistorico
 * - Comunica ações via callbacks
 */

import { useState } from 'react';
import {
  FileText,
  File,
  Image,
  Trash2,
  Eye,
  AlertCircle,
  CheckCircle,
  Clock,
  Loader,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import type { DocumentoListado } from '../../tipos/tiposDocumentos';
import type {
  ConfiguracaoPaginacao,
  ConfiguracaoOrdenacao,
  AcaoDocumento,
} from '../../tipos/tiposHistorico';
import {
  TipoDocumento,
  StatusProcessamento,
  formatarTamanhoArquivo,
} from '../../tipos/tiposDocumentos';


// ===== INTERFACES =====

/**
 * Propriedades do ComponenteListaDocumentos
 */
interface PropriedadesComponenteListaDocumentos {
  /** Documentos a exibir na página atual */
  documentos: DocumentoListado[];
  
  /** Configuração de paginação */
  paginacao: ConfiguracaoPaginacao;
  
  /** Configuração de ordenação */
  ordenacao: ConfiguracaoOrdenacao;
  
  /** Callback chamado quando página muda */
  onPaginaMudar: (novaPagina: number) => void;
  
  /** Callback chamado quando usuário clica em uma ação */
  onAcao: (acao: AcaoDocumento, idDocumento: string, nomeArquivo: string) => void;
  
  /** Se a lista está em estado de carregamento */
  carregando?: boolean;
  
  /** Se não há documentos (estado vazio inicial) */
  estadoVazio?: boolean;
  
  /** Se filtros não retornaram resultados */
  semResultadosFiltros?: boolean;
}


// ===== COMPONENTE PRINCIPAL =====

/**
 * Componente de listagem de documentos com paginação
 */
export function ComponenteListaDocumentos({
  documentos,
  paginacao,
  onPaginaMudar,
  onAcao,
  carregando = false,
  estadoVazio = false,
  semResultadosFiltros = false,
}: PropriedadesComponenteListaDocumentos) {
  
  // Estado: documento sendo deletado (para confirmação)
  const [documentoParaDeletar, setDocumentoParaDeletar] = useState<{
    id: string;
    nome: string;
  } | null>(null);


  // ===== FUNÇÕES AUXILIARES =====

  /**
   * Retorna ícone apropriado para tipo de documento
   */
  const obterIconeTipoDocumento = (tipo: string) => {
    switch (tipo) {
      case TipoDocumento.PDF:
        return <FileText className="size-5 text-red-600" />;
      case TipoDocumento.DOCX:
        return <File className="size-5 text-blue-600" />;
      case TipoDocumento.PNG:
      case TipoDocumento.JPG:
      case TipoDocumento.JPEG:
        return <Image className="size-5 text-green-600" />;
      default:
        return <File className="size-5 text-gray-600" />;
    }
  };

  /**
   * Retorna badge colorido para status de processamento
   */
  const obterBadgeStatus = (status: StatusProcessamento | undefined) => {
    if (status === undefined) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800">
          <AlertCircle className="size-3" />
          Indefinido
        </span>
      );
    }
    switch (status) {
      case StatusProcessamento.CONCLUIDO:
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
            <CheckCircle className="size-3" />
            Concluído
          </span>
        );
      case StatusProcessamento.PROCESSANDO:
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
            <Loader className="size-3 animate-spin" />
            Processando
          </span>
        );
      case StatusProcessamento.PENDENTE:
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800">
            <Clock className="size-3" />
            Pendente
          </span>
        );
      case StatusProcessamento.ERRO:
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
            <AlertCircle className="size-3" />
            Erro
          </span>
        );
      default:
        return (
          <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800">
            {status}
          </span>
        );
    }
  };

  /**
   * Formata data para exibição
   */
  const formatarData = (dataISO: string): string => {
    const data = new Date(dataISO);
    return data.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };


  // ===== HANDLERS =====

  /**
   * Inicia confirmação de deleção
   */
  const iniciarDelecao = (idDocumento: string, nomeArquivo: string) => {
    setDocumentoParaDeletar({ id: idDocumento, nome: nomeArquivo });
  };

  /**
   * Confirma deleção
   */
  const confirmarDelecao = () => {
    if (documentoParaDeletar) {
      onAcao('deletar', documentoParaDeletar.id, documentoParaDeletar.nome);
      setDocumentoParaDeletar(null);
    }
  };

  /**
   * Cancela deleção
   */
  const cancelarDelecao = () => {
    setDocumentoParaDeletar(null);
  };


  // ===== ESTADOS ESPECIAIS =====

  // Estado: Carregando
  if (carregando) {
    return (
      <div className="card p-12 text-center">
        <Loader className="mx-auto mb-4 size-8 animate-spin text-indigo-600" />
        <p className="text-gray-600">Carregando documentos...</p>
      </div>
    );
  }

  // Estado: Nenhum documento no sistema
  if (estadoVazio) {
    return (
      <div className="card p-12 text-center">
        <FileText className="mx-auto mb-4 size-16 text-gray-300" />
        <h3 className="mb-2 text-lg font-semibold text-gray-900">
          Nenhum documento encontrado
        </h3>
        <p className="mb-4 text-gray-600">
          Você ainda não fez upload de nenhum documento.
        </p>
        <a
          href="/upload"
          className="btn-primary inline-flex items-center gap-2"
        >
          Fazer Upload de Documentos
        </a>
      </div>
    );
  }

  // Estado: Filtros não retornaram resultados
  if (semResultadosFiltros) {
    return (
      <div className="card p-12 text-center">
        <AlertCircle className="mx-auto mb-4 size-16 text-gray-300" />
        <h3 className="mb-2 text-lg font-semibold text-gray-900">
          Nenhum documento corresponde aos filtros
        </h3>
        <p className="text-gray-600">
          Tente ajustar ou limpar os filtros para ver mais resultados.
        </p>
      </div>
    );
  }


  // ===== RENDER PRINCIPAL =====

  return (
    <div className="space-y-4">
      {/* Tabela de documentos */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Documento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Tamanho
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Data de Upload
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {documentos.map((documento) => (
                <tr
                  key={documento.idDocumento}
                  className="transition-colors hover:bg-gray-50"
                >
                  {/* Coluna: Nome do arquivo */}
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex items-center gap-3">
                      {obterIconeTipoDocumento(documento.tipoDocumento)}
                      <div className="min-w-0 flex-1">
                        <div className="max-w-xs truncate text-sm font-medium text-gray-900">
                          {documento.nomeArquivo}
                        </div>
                        {documento.numeroChunks && (
                          <div className="text-xs text-gray-500">
                            {documento.numeroChunks} chunks
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Coluna: Tipo */}
                  <td className="whitespace-nowrap px-6 py-4">
                    <span className="text-sm uppercase text-gray-700">
                      {documento.tipoDocumento}
                    </span>
                  </td>

                  {/* Coluna: Tamanho */}
                  <td className="whitespace-nowrap px-6 py-4">
                    <span className="text-sm text-gray-700">
                      {formatarTamanhoArquivo(documento.tamanhoEmBytes)}
                    </span>
                  </td>

                  {/* Coluna: Data */}
                  <td className="whitespace-nowrap px-6 py-4">
                    <span className="text-sm text-gray-700">
                      {formatarData(documento.dataHoraUpload)}
                    </span>
                  </td>

                  {/* Coluna: Status */}
                  <td className="whitespace-nowrap px-6 py-4">
                    {obterBadgeStatus(documento.statusProcessamento)}
                  </td>

                  {/* Coluna: Ações */}
                  <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {/* Botão: Visualizar detalhes */}
                      <button
                        type="button"
                        onClick={() =>
                          onAcao('visualizar', documento.idDocumento, documento.nomeArquivo)
                        }
                        className="rounded p-1 text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900"
                        title="Visualizar detalhes"
                      >
                        <Eye className="size-4" />
                      </button>

                      {/* Botão: Deletar */}
                      <button
                        type="button"
                        onClick={() =>
                          iniciarDelecao(documento.idDocumento, documento.nomeArquivo)
                        }
                        className="rounded p-1 text-red-600 transition-colors hover:bg-red-50 hover:text-red-900"
                        title="Deletar documento"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginação */}
      {paginacao.totalPaginas > 1 && (
        <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
          {/* Informação de página */}
          <div className="text-sm text-gray-700">
            Página <span className="font-medium">{paginacao.paginaAtual}</span> de{' '}
            <span className="font-medium">{paginacao.totalPaginas}</span>
            {' • '}
            <span className="font-medium">{paginacao.totalItens}</span> documento
            {paginacao.totalItens !== 1 ? 's' : ''} no total
          </div>

          {/* Botões de navegação */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onPaginaMudar(paginacao.paginaAtual - 1)}
              disabled={paginacao.paginaAtual === 1}
              className="btn-secondary px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft className="size-4" />
            </button>
            
            <button
              type="button"
              onClick={() => onPaginaMudar(paginacao.paginaAtual + 1)}
              disabled={paginacao.paginaAtual === paginacao.totalPaginas}
              className="btn-secondary px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
        </div>
      )}

      {/* Modal de confirmação de deleção */}
      {documentoParaDeletar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md animate-fadeIn rounded-lg bg-white p-6 shadow-xl">
            <div className="flex items-start gap-4">
              <div className="shrink-0">
                <AlertCircle className="size-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="mb-2 text-lg font-semibold text-gray-900">
                  Confirmar Deleção
                </h3>
                <p className="mb-4 text-sm text-gray-600">
                  Tem certeza que deseja deletar o documento{' '}
                  <span className="font-medium">{documentoParaDeletar.nome}</span>?
                </p>
                <p className="mb-6 text-sm text-gray-600">
                  Esta ação não pode ser desfeita. O documento e seus chunks vetorizados
                  serão permanentemente removidos.
                </p>
                
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={cancelarDelecao}
                    className="btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={confirmarDelecao}
                    className="rounded-lg bg-red-600 px-4 py-2 font-medium text-white transition-colors hover:bg-red-700"
                  >
                    Deletar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
